import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Box, Paper, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { AiOutlinePlus } from 'react-icons/ai';
import { FiSearch, FiUsers } from 'react-icons/fi';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';

import { ConfirmDialog } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';
import { RolesSaveBarProvider } from '@vyce/core/src/contexts/rolesSaveBarProvider';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { useQuery } from '@vyce/core/src/hooks/useQuery';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { Company, MANAGEMENT_PERMISSIONS, StaffListItem } from '@vyce/core/src/types';
import { SendStaffInviteRequest, UpdateStaffPermissionsPayload } from '@vyce/core/src/api/types';
import { useRolesStyles } from '@vyce/core/src/modules/rolesModule/styles';
import { RolesEmptyState } from '@vyce/core/src/modules/rolesModule/components/RolesEmptyState';
import { RolesPageSaveBar } from '@vyce/core/src/modules/rolesModule/components/RolesPageSaveBar';

import { StaffAccordionRow } from './components/StaffAccordionRow';
import { AdminInviteDialog } from './components/admin-invite/AdminInviteDialog';
import { useStaffAccordionStyles } from './accordionStyles';

const PAGE_SIZE = 25;

interface Props {
  selectedCompanyId: string | null;
  /** The Add Admin dialog needs these; the accordion itself reads the roles context. */
  updateStaffPermissionsRequest: (
    companyId: string,
    userId: string,
    data: UpdateStaffPermissionsPayload
  ) => Promise<AxiosResponse<unknown>>;
  createStaffInviteRequest: (
    companyId: string,
    payload: SendStaffInviteRequest
  ) => Promise<AxiosResponse<unknown>>;
  removeStaffMemberRequest: (companyId: string, userId: string) => Promise<AxiosResponse<unknown>>;
  /** Legend invites into any company, so the dialog needs the list to pick from. */
  companies?: Company[];
  /** Legend links each name through to that admin's profile page. */
  getProfileLink?: (staff: StaffListItem) => string;
}

/**
 * Company Admins as an accordion: one row per admin/manager, expanding into the
 * module → role → permission editor, edited inline. Replaces the old data grid
 * plus its "Edit Permission" dialog — the dialog is now invite-only.
 */
export const StaffPermissionsAccordion: React.FC<Props> = ({
  selectedCompanyId,
  updateStaffPermissionsRequest,
  createStaffInviteRequest,
  removeStaffMemberRequest,
  companies,
  getProfileLink,
}) => {
  const classes = useRolesStyles();
  const listClasses = useStaffAccordionStyles();
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError } = useContext(NotificationContext);
  const { getStaffRequest } = useContext(RolesModuleContext);
  const { can } = usePermissions();

  const canManageStaffRoles = can(MANAGEMENT_PERMISSIONS.MANAGE_STAFF_ROLES);
  // Staff invites need can_manage_staff_roles (owner / company admin); can_invite_workers
  // only covers workers now, so a module manager would get a 403 from Add Admin.
  const canInvite = canManageStaffRoles;
  // The permission catalogue endpoint accepts either right. Without one of them a
  // row can't be expanded at all — the catalogue GET would 403 and the panel
  // would report a load failure, which is the wrong story to tell.
  const canReadPermissions = canManageStaffRoles || can(MANAGEMENT_PERMISSIONS.READ_PERMISSIONS);

  const { substring, total, setTotal, handleSearchChange } = useTable({});
  const [staff, setStaff] = useState<StaffListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const history = useHistory();
  const query = useQuery();
  // ?invite=1 opens the Add Admin dialog, as a Laila card's "Open full form" does
  const inviteLink = !!query.get('invite');
  const [userToRemove, setUserToRemove] = useState<StaffListItem>();
  const [removing, setRemoving] = useState(false);

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!selectedCompanyId) return;
      const setBusy = offset === 0 ? setLoading : setLoadingMore;
      try {
        setBusy(true);
        const res = await getStaffRequest(selectedCompanyId, {
          substring,
          limit: PAGE_SIZE,
          offset,
        });
        const items = res.data.items || [];
        // Append on "Load more", replace on a fresh search — expanded rows below
        // the fold keep their state because the earlier items keep their identity.
        setStaff(prev => (offset === 0 ? items : [...prev, ...items]));
        setTotal(res.data?.count || 0);
      } catch (e) {
        handleServerError(e);
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompanyId, substring, getStaffRequest]
  );

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  const handleRemove = async () => {
    if (!selectedCompanyId || !userToRemove) return;
    try {
      setRemoving(true);
      await removeStaffMemberRequest(selectedCompanyId, userToRemove.user_id);
      setUserToRemove(undefined);
      fetchPage(0);
    } catch (e) {
      handleServerError(e);
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    if (inviteLink && canInvite) {
      setInviteOpen(true);
    }
  }, [inviteLink, canInvite]);

  const closeInvite = () => {
    setInviteOpen(false);
    // Drop the link that opened it, so a refresh doesn't open it again
    if (inviteLink) {
      history.replace({ pathname: history.location.pathname });
    }
  };

  const hasMore = staff.length < total;

  return (
    <RolesSaveBarProvider>
      <Box className={listClasses.toolbar}>
        <AppSearchInput onChange={handleSearchChange} isSmall smallRounding expanded={!isMobile} />

        {canInvite && (
          <AppButton
            size="small"
            onClick={() => setInviteOpen(true)}
            startIcon={<AiOutlinePlus />}
            variant="contained"
            color="primary">
            Add Admin
          </AppButton>
        )}
      </Box>

      <Paper variant="outlined" className={classes.paper}>
        {loading ? (
          [0, 1, 2].map(index => (
            <Box key={index} className={listClasses.skeletonRow}>
              <Skeleton variant="rect" height={44} style={{ borderRadius: 12 }} />
            </Box>
          ))
        ) : !selectedCompanyId ? (
          <RolesEmptyState
            icon={<HiOutlineOfficeBuilding size={28} />}
            title="Select a company"
            subtitle="Pick a company above to see who administers it and what they can do."
          />
        ) : staff.length === 0 ? (
          <RolesEmptyState
            icon={substring ? <FiSearch size={26} /> : <FiUsers size={26} />}
            title={substring ? 'Nothing matches that search' : 'No admins yet'}
            subtitle={
              substring
                ? 'Try another name or email address.'
                : canInvite
                ? 'Invite someone to give them access to this company’s modules.'
                : 'Company admins invited by the owner will show here.'
            }
          />
        ) : (
          <>
            {staff.map(item => (
              <StaffAccordionRow
                key={item.user_id}
                staff={item}
                companyId={selectedCompanyId}
                canExpand={canReadPermissions}
                canRemove={canManageStaffRoles}
                profileLink={getProfileLink?.(item)}
                onRemoveClick={setUserToRemove}
              />
            ))}

            {hasMore && (
              <div className={listClasses.loadMoreRow}>
                <AppButton
                  size="small"
                  loading={loadingMore}
                  loaderVariant="paper"
                  disabled={loadingMore}
                  onClick={() => fetchPage(staff.length)}>
                  {`Show more — ${total - staff.length} left`}
                </AppButton>
              </div>
            )}
          </>
        )}
      </Paper>

      {!loading && !!staff.length && (
        <Box marginTop={1}>
          <Typography variant="caption" color="textSecondary">
            Showing {staff.length} of {total}
          </Typography>
        </Box>
      )}

      {/* Adds a new admin — fine-grained edits happen inline in the expanded row */}
      <AdminInviteDialog
        open={inviteOpen}
        onClose={closeInvite}
        onSuccess={() => fetchPage(0)}
        companyId={selectedCompanyId}
        companies={companies}
        updateStaffPermissionsRequest={updateStaffPermissionsRequest}
        createStaffInviteRequest={createStaffInviteRequest}
      />

      <ConfirmDialog
        open={!!userToRemove}
        title="Are you sure you want to remove this admin?"
        confirmText="Remove"
        cancelText="Cancel"
        loading={removing}
        handleClose={() => setUserToRemove(undefined)}
        handleConfirm={handleRemove}
      />

      {/* Last child on purpose: a sticky bar pins to the viewport for as long as
          its containing block runs below the fold, so parking it at the end gives
          "follows you down the list, then settles at the bottom of the page". */}
      <RolesPageSaveBar />
    </RolesSaveBarProvider>
  );
};
